import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useToast } from '@/components/ui/use-toast';
import useLoyaltyStore from '@/lib/zustand/store/loyaltyStore';
import useNotificationStore from '@/lib/zustand/store/notificationStore';
import useOneOnOneStore from '@/lib/zustand/store/oneOnOneStore';
import { useCourseStore } from '@/lib/zustand/store/store';
import { createOrder } from '@/screens/payment/actions';
import { getProfile } from '@/screens/profile/actions';
import { sendGTMEvent } from '@next/third-parties/google';
import { useSession } from 'next-auth/react';
import doneLottie from '../../public/lottie/Done-Lottie.json';
import rewardlottie from '../../public/lottie/Reward-Lottie3.json';
import useDialog from './useDialog';

export const usePayment = () => {
  const { fetchLoyalty, xp, coin } = useLoyaltyStore();
  const { fetchNotification } = useNotificationStore();
  const { setModal, setSelectedItem } = useOneOnOneStore();
  const { show } = useDialog({ name: 'levelUp' });
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { playList, fetchPlayList } = useCourseStore(state => state);
  const { data } = useSession();
  const router = useRouter();
  const [paymentId, setPaymentId] = useState<null | string>(null);
  const [oneOnOnepaymentId, setOneOnOnepaymentId] = useState<null | string>(null);

  useEffect(() => {
    if (!paymentId) return;

    const maxRetries = 5;
    let retryCount = 0;

    const fetchOrderStatus = async () => {
      try {
        const response = await fetch(
          `/api/razorpay/polling?paymentId=${paymentId}&currentXp=${xp}&currentCoin=${coin}`,
          {
            method: 'GET',
          }
        );

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const responseData = await response.json();

        if (responseData.status === 200) {
          fetchPlayList(playList.slug); //update playlist
          clearInterval(interval);
          setPaymentId(null);

          if (responseData?.loyaltyDetails) {
            toast({
              title: `Woohoo🎉  ${responseData.loyaltyDetails.newCoin} Coins & ${responseData.loyaltyDetails.newXp} XP!`,
              description: "You've gained more XP and Coins!",
              icon: rewardlottie,
            });

            if (responseData.loyaltyDetails.isUpgraded) {
              show(); // Show level-up confetti dialog
            }

            router.push('/my-learnings');
            await fetchLoyalty(); // Update store
          }
        }
      } catch (error) {
        console.error('Error fetching order status:', error);

        retryCount += 1;
        if (retryCount >= maxRetries) {
          setPaymentId(null);
          clearInterval(interval);
          toast({
            title: 'Payment Processing Issue',
            description:
              'An issue occurred while processing your payment. Please refresh the page or contact support if this continues.',
            variant: 'destructive',
          });
        }
      }
    };

    const interval = setInterval(fetchOrderStatus, 2000);
    return () => clearInterval(interval);
  }, [paymentId, xp, coin]);

  const handlePayment = async () => {
    if (!data) {
      sendGTMEvent({
        event: 'enrollButtonClicked',
        value: 'Guest',
      });

      return;
    }
    sendGTMEvent({
      event: 'enrollButtonClicked',
      value: 'User',
    });

    const user: any = await getProfile();

    setLoading(true);

    try {
      const response = await fetch('/api/razorpay/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: playList.offerPrice, courseId: playList.id }),
      });

      const order = await response.json();
      if (response.ok) {
        const options = {
          key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
          amount: order.amount,
          currency: order.currency,
          name: user?.firstName + ' ' + user?.lastName,
          description: playList?.title,
          notes: {
            type: 'Course',
            course_id: playList?.id,
            user_id: data?.user?.userId,
          },
          order_id: order.id,
          handler: async function (response: any) {
            const payload = {
              userId: data?.user?.userId,
              email: data?.user?.email,
              courseId: playList?.id || 0,
              paymentId: response?.razorpay_payment_id,
            };

            if (process.env.NODE_ENV !== 'production') {
              const orderStatus: any = await createOrder(payload);
              if (orderStatus?.purchaseCoinStatus?.newCoinBalance) {
                toast({
                  title: `🎉 Woohoo! ${Math.ceil(orderStatus?.purchaseCoinStatus?.newCoinBalance)} Coins & ${Math.ceil(orderStatus?.xpStatus?.newXpBalance)} XP!`,
                  description: "You've gained more xp and coins!",
                  icon: rewardlottie,
                });
              }
              if (orderStatus?.xpStatus?.levelStatus.isUpgraded) {
                show(); //show lelev upgraded confetti dialog
              }
              router.push('/my-learnings');
              fetchLoyalty(); //update store
              fetchPlayList(playList.slug); //update playlist
            } else if (process.env.NODE_ENV === 'production') {
              setPaymentId(response.razorpay_payment_id); //setting for long polling
            }
          },
          prefill: {
            name: user?.firstName + ' ' + user?.lastName,
            email: user?.email,
            // contact: user?.phone,
          },
        };

        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
      } else {
        alert('Error creating order: ' + order.error);
      }
    } catch (error) {
      alert('Error creating order: ' + error);
    } finally {
      setLoading(false);
    }
  };

  type OneOnOneProp = {
    id: number;
    amount: number;
    description: string;
    date: Date;
    via: string;
    link: string;
    email: string;
    start: string;
    name: string;
    location: string;
    topic: string;
    end: string;
    duration: string;
    timeZone: string;
  };
  const handleOneOnOnePayment = async ({
    id,
    amount,
    description,
    topic,
    date,
    via,
    link,
    name,
    start,
    location,
    end,
    duration,
    timeZone,
  }: OneOnOneProp): Promise<any> => {
    if (!data) return Promise.reject(new Error('User data is unavailable.'));

    const user: any = await getProfile();
    setLoading(true);

    try {
      const response = await fetch('/api/razorpay/create-order/one-on-one', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, id }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error?.message || 'Failed to create order.');
      }

      const order = await response.json();

      return new Promise((resolve, reject) => {
        const options = {
          key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
          amount: order.amount,
          currency: order.currency,
          name: `${user?.firstName} ${user?.lastName}`,
          description,
          notes: {
            type: 'OneOnOne',
            one_on_one_id: order?.id,
            one_on_one_topic: topic,
            user_id: data?.user?.userId,
            name,
            start,
            end,
            timeZone,
            location,
            duration,
          },
          order_id: order.id,
          handler: async function (paymentResponse: any) {
            try {
              const payload = {
                email: data?.user?.email,
                topic,
                date,
                via,
                link,
                paymentId: paymentResponse?.razorpay_payment_id,
              };

              if (process.env.NODE_ENV !== 'production') {
                const orderStatusResponse = await fetch('/api/one-on-one/orders', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });

                if (orderStatusResponse.ok) {
                  const orderStatus = await orderStatusResponse.json();
                  resolve(orderStatus);
                } else {
                  const orderError = await orderStatusResponse.json();
                  reject(new Error(orderError?.message || 'Failed to confirm payment.'));
                }
              } else if (process.env.NODE_ENV === 'production') {
                setOneOnOnepaymentId(paymentResponse.razorpay_payment_id);
              }
            } catch (error) {
              reject(error);
            }
          },
          prefill: {
            name: `${user?.firstName} ${user?.lastName}`,
            email: user?.email,
            // contact: user?.phone,
          },
          theme: {
            color: '#00FFD3',
          },
          modal: {
            ondismiss: function () {
              reject(new Error('Payment process was canceled.'));
            },
          },
        };

        const paymentObject = new window.Razorpay(options);

        paymentObject.open();
      });
    } catch (error) {
      console.error('Error in handleOneOnOnePayment:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!oneOnOnepaymentId) return;

    const maxRetries = 5;
    let retryCount = 0;

    const fetchOneOnOneOrderStatus = async () => {
      try {
        const response = await fetch(`/api/one-on-one/polling?paymentId=${oneOnOnepaymentId}`, {
          method: 'GET',
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const responseData = await response.json();

        if (responseData.status === 200) {
          fetchNotification();
          toast({
            title: `Appointment Request Sent`,
            description: `Thank you for your request! I'll be in touch shortly with further details.`,
            icon: doneLottie,
          });
          setModal('closed');
          setSelectedItem(null);
        }
      } catch (error) {
        console.error('Error fetching order status:', error);

        retryCount += 1;
        if (retryCount >= maxRetries) {
          setPaymentId(null);
          clearInterval(interval);
          toast({
            title: 'Payment Processing Issue',
            description:
              'An issue occurred while processing your payment. Please refresh the page or contact support if this continues.',
            variant: 'destructive',
          });
        }
      }
    };

    const interval = setInterval(fetchOneOnOneOrderStatus, 2000);
    return () => clearInterval(interval);
  }, [oneOnOnepaymentId]);

  return { handlePayment, loading, paymentId, oneOnOnepaymentId, handleOneOnOnePayment };
};
